import { FullSizeSpinner } from '@/components/full-size-spinner';
import { PageWrapper } from '@/components/page-wrapper/page-wrapper';
import { ContentSwitcher, InlineAlert } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import type { ReactNode } from 'react';
import { useState, useSyncExternalStore } from 'react';
import { AutoCalcLogsPanel } from './components/autocalc-logs-panel';
import { DashboardSection } from './components/dashboard-section';
import { FilterableEventInboxTable } from './components/filterable-event-inbox-table';
import { FilterablePriceRowsTable } from './components/filterable-price-rows-table';
import { IngestionStatusPanel } from './components/ingestion-status-panel';
import { OverviewPanel } from './components/overview-panel';
import { SettingsTable } from './components/settings-table';
import {
  BackendErrorsTable,
  FrontendErrorsTable,
} from './components/telemetry-errors-tables';
import { UsersTable } from './components/users-table';
import { SETTINGS_RISK_MESSAGE } from './dev-dashboard-helpers';
import {
  useDevBackendErrors,
  useDevIngestionStatus,
  useDevOverview,
  useDevSettings,
  useDevUsers,
} from './dev-dashboard-queries';
import { frontendErrorStore } from './frontend-error-store';

type QueryState<T> = {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
};

/** Shows a spinner, an error text, or the content of a query. */
const QueryContent = <T,>({
  query,
  children,
}: {
  query: QueryState<T>;
  children: (data: T) => ReactNode;
}) => {
  if (query.isPending) return <FullSizeSpinner />;
  if (query.isError || query.data === undefined) {
    return <Text>Could not load the data. It reloads automatically.</Text>;
  }
  return <>{children(query.data)}</>;
};

export const DEV_DASHBOARD_TABS = [
  'Overview',
  'Settings',
  'ETL Ingestion',
  'Price Events',
  'Event Inbox',
  'BW Exports',
  'Autocalc Logs',
  'Backend Errors',
  'Frontend Errors',
  'Users',
] as const;
export type DevDashboardTab = (typeof DEV_DASHBOARD_TABS)[number];

/** Tables are at most this wide, centered on wide screens. */
const CONTENT_MAX_WIDTH = '1440px';

const OverviewTab = () => {
  const overview = useDevOverview();
  return (
    <Flex flexDirection="column" flex={1} minHeight={0} overflowY="auto">
      <QueryContent query={overview}>
        {(data) => <OverviewPanel overview={data} />}
      </QueryContent>
    </Flex>
  );
};

const UsersTab = () => {
  const users = useDevUsers();
  return (
    <DashboardSection title="Users">
      <Text fontSize="sm">
        Roles are from the last login of each user. The IdP is the source of
        truth.
      </Text>
      <QueryContent query={users}>
        {(data) => <UsersTable users={data} />}
      </QueryContent>
    </DashboardSection>
  );
};

const SettingsTab = () => {
  const settings = useDevSettings();
  return (
    <DashboardSection title="Settings">
      <InlineAlert showIcon variant="negative">
        <InlineAlert.Title>Be extremely careful</InlineAlert.Title>
        <InlineAlert.Content>{SETTINGS_RISK_MESSAGE}</InlineAlert.Content>
      </InlineAlert>
      <QueryContent query={settings}>
        {(data) => <SettingsTable settings={data} />}
      </QueryContent>
    </DashboardSection>
  );
};

const IngestionTab = () => {
  const ingestionStatus = useDevIngestionStatus();
  return (
    <DashboardSection title="ETL ingestion">
      <QueryContent query={ingestionStatus}>
        {(data) => <IngestionStatusPanel status={data} />}
      </QueryContent>
    </DashboardSection>
  );
};

const BackendErrorsTab = () => {
  const backendErrors = useDevBackendErrors();
  return (
    <DashboardSection title="Backend errors (OpenTelemetry)">
      <Text fontSize="sm">
        Failed spans of the backend instance that answered. The full history is
        in Grafana and Elastic: search by trace ID.
      </Text>
      <QueryContent query={backendErrors}>
        {(data) => <BackendErrorsTable errors={data} />}
      </QueryContent>
    </DashboardSection>
  );
};

const FrontendErrorsTab = () => {
  const frontendErrors = useSyncExternalStore(
    frontendErrorStore.subscribe,
    frontendErrorStore.getAll,
  );
  return (
    <DashboardSection title="Frontend errors and warnings">
      <Text fontSize="sm">
        Errors and warnings of this browser tab. The full history is in Sentry.
      </Text>
      <FrontendErrorsTable errors={frontendErrors} />
    </DashboardSection>
  );
};

const TabContent = ({ tab }: { tab: DevDashboardTab }) => {
  switch (tab) {
    case 'Overview':
      return <OverviewTab />;
    case 'Settings':
      return <SettingsTab />;
    case 'ETL Ingestion':
      return <IngestionTab />;
    case 'Price Events':
      return (
        <DashboardSection title="Last 50 price events">
          <FilterablePriceRowsTable
            kind="priceEvents"
            idHeader="Price Event Id"
          />
        </DashboardSection>
      );
    case 'Event Inbox':
      return (
        <DashboardSection title="Last 50 event inbox messages">
          <FilterableEventInboxTable />
        </DashboardSection>
      );
    case 'BW Exports':
      return (
        <DashboardSection title="Last 50 prices to BW">
          <FilterablePriceRowsTable kind="bwPrices" idHeader="Price Log Id" />
        </DashboardSection>
      );
    case 'Autocalc Logs':
      return (
        <DashboardSection title="Autocalc fail logs">
          <AutoCalcLogsPanel />
        </DashboardSection>
      );
    case 'Backend Errors':
      return <BackendErrorsTab />;
    case 'Frontend Errors':
      return <FrontendErrorsTab />;
    case 'Users':
      return <UsersTab />;
  }
};

/**
 * Internal developer dashboard. It is only available at /dev/dashboard, for users with the
 * AccessTestEndpoints permission. Each tab loads only its own data, and reloads it automatically.
 */
export const DevDashboardPage = () => {
  const [activeTab, setActiveTab] = useState<DevDashboardTab>('Overview');

  return (
    <PageWrapper
      headerProps={{ title: 'Developer dashboard' }}
      layout="fill"
      maxWidth="100%"
    >
      <Flex
        flexDirection="column"
        gap={3}
        height="100%"
        paddingY={3}
        width="100%"
        maxWidth={CONTENT_MAX_WIDTH}
        marginX="auto"
      >
        <ContentSwitcher
          aria-label="Developer dashboard sections"
          variant="emphasis"
          size="small"
          onSwitch={(value) => setActiveTab(value as DevDashboardTab)}
        >
          {DEV_DASHBOARD_TABS.map((tab) => (
            <ContentSwitcher.Segment
              key={tab}
              selected={activeTab === tab}
              aria-controls="dev-dashboard-tab-content"
              text={tab}
              value={tab}
            />
          ))}
        </ContentSwitcher>
        <Flex
          id="dev-dashboard-tab-content"
          flexDirection="column"
          flex={1}
          minHeight={0}
        >
          <TabContent tab={activeTab} />
        </Flex>
      </Flex>
    </PageWrapper>
  );
};
